import { useState, useEffect, useRef } from 'react';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

export default function CookieSettingsModal() {
  const { showSettingsModal, closeSettingsModal, saveCustomPreferences, preferences } = useCookieConsent();
  const [localPrefs, setLocalPrefs] = useState(preferences);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setLocalPrefs(preferences);
  }, [preferences]);

  useEffect(() => {
    if (showSettingsModal) {
      document.body.style.overflow = 'hidden';
      firstFocusableRef.current?.focus();

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closeSettingsModal();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleEscape);
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [showSettingsModal, closeSettingsModal]);

  if (!showSettingsModal) return null;

  const handleSave = () => {
    saveCustomPreferences(localPrefs);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeSettingsModal();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="relative mx-4 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl animate-scaleIn"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
      >
        <button
          ref={firstFocusableRef}
          onClick={closeSettingsModal}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
          aria-label="Kapat"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <h2 id="cookie-settings-title" className="mb-6 text-2xl font-bold text-gray-900">
          Çerez Tercihlerini Yönet
        </h2>

        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="mb-1 font-semibold text-gray-900">
                  Zorunlu Çerezler
                </h3>
                <p className="text-sm text-gray-600">
                  Sitenin çalışması için gereklidir. Bu çerezler devre dışı bırakılamaz.
                </p>
              </div>
              <div className="ml-4 flex h-6 w-11 items-center rounded-full bg-blue-600">
                <div className="h-5 w-5 translate-x-6 rounded-full bg-white shadow-md"></div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="mb-1 font-semibold text-gray-900">
                  Analitik Çerezler
                </h3>
                <p className="text-sm text-gray-600">
                  Ziyaretçi istatistikleri ile deneyimi iyileştirir.
                </p>
              </div>
              <button
                onClick={() => setLocalPrefs({ ...localPrefs, analytics: !localPrefs.analytics })}
                className={`ml-4 flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                  localPrefs.analytics ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={localPrefs.analytics}
              >
                <div
                  className={`h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
                    localPrefs.analytics ? 'translate-x-6' : 'translate-x-1'
                  }`}
                ></div>
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="mb-1 font-semibold text-gray-900">
                  Pazarlama Çerezleri
                </h3>
                <p className="text-sm text-gray-600">
                  İçerikleri ve kampanyaları kişiselleştirmeye yardımcı olur.
                </p>
              </div>
              <button
                onClick={() => setLocalPrefs({ ...localPrefs, marketing: !localPrefs.marketing })}
                className={`ml-4 flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                  localPrefs.marketing ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={localPrefs.marketing}
              >
                <div
                  className={`h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
                    localPrefs.marketing ? 'translate-x-6' : 'translate-x-1'
                  }`}
                ></div>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            onClick={closeSettingsModal}
            className="whitespace-nowrap rounded-lg border-2 border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-98 transition-all duration-140 cursor-pointer"
          >
            Vazgeç
          </button>
          <button
            onClick={handleSave}
            className="whitespace-nowrap rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 active:scale-98 transition-all duration-140 shadow-lg hover:shadow-xl cursor-pointer"
          >
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}